import { BrowserRouter as Router, NavLink, Routes, Route, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { useAuth } from 'react-oidc-context'
import { setAccessToken } from './auth/tokenStore'
import HomePage from './pages/HomePage'
import CoursesPage from './pages/CoursesPage'
import CourseDetailPage from './pages/CourseDetailPage'
import CourseEditorPage from './pages/CourseEditorPage'
import EditGPSPage from './pages/EditGPSPage'
import CourseMapperPage from './pages/CourseMapperPageV2'
import ContributeCoursePage from './pages/ContributeCoursePage'
import DashboardPage from './pages/DashboardPage'
import StatsPage from './pages/RecordedStatsPage'
import RoundsPage from './pages/ActiveRoundsPageV2'
import NewRoundPage from './pages/NewRoundPage'
import RoundDetailsPage from './pages/RoundDetailsPage'
import AppBrand from './components/AppBrand'
import CourseGeometryImportPage from './pages/CourseGeometryImportPage'
import PhoneRoundPage from './pages/PhoneRoundPage'
import FourStrokeHoleDemo from './components/FourStrokeHoleDemo'
import RoundReplayFixturePage from './pages/RoundReplayFixturePageV2'
import MappedRoundFixturePage from './pages/MappedRoundFixturePage'
import MobileCourseMapPage from './pages/MobileCourseMapPage'

// Shows the user's name + sign-out when authenticated, or a sign-in button otherwise.
// golfapp has no shared nav bar (each page renders its own layout), so this floats in
// the top-right corner across all pages — the faithful equivalent of ColorCraft's nav slot.
function AuthButton() {
  const auth = useAuth()

  if (auth.isLoading) {
    return <span className="text-gray-400 text-sm">…</span>
  }

  if (auth.isAuthenticated) {
    const name = auth.user?.profile.given_name || auth.user?.profile.name || auth.user?.profile.email
    return (
      <div className="flex items-center space-x-3">
        <span className="hidden sm:inline text-gray-600 dark:text-gray-300 text-sm">Hi, {name}</span>
        <button
          onClick={() => auth.signoutRedirect()}
          className="text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400 font-medium transition-colors"
        >
          Sign out
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => auth.signinRedirect()}
      className="bg-gradient-to-r from-golf-600 to-emerald-600 text-white px-5 py-2 rounded-lg font-medium hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
    >
      Sign in
    </button>
  )
}

function AppHeader() {
  const location=useLocation()
  if(location.pathname==='/mobile-course-map') return null
  const navClass = ({isActive}:{isActive:boolean}) => `rounded-lg px-3 py-2 text-sm font-medium transition ${isActive ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900 dark:text-emerald-100' : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'}`
  return <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-950/95"><div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-2 px-3 py-2"><AppBrand compact /><nav aria-label="Primary" className="order-3 flex w-full gap-1 overflow-x-auto sm:order-none sm:w-auto"><NavLink to="/courses" className={navClass}>Courses</NavLink><NavLink to="/courses/contribute" className={navClass}>Contribute</NavLink><NavLink to="/rounds" className={navClass}>Rounds</NavLink><NavLink to="/stats" className={navClass}>Stats</NavLink></nav><AuthButton /></div></header>
}

// Rendered at /callback while the OIDC redirect is being processed.
function AuthCallback() {
  const auth = useAuth()
  const navigate = useNavigate()

  // Once react-oidc-context has finished the code->token exchange, hand control back
  // to react-router so the SPA leaves /callback and the query string is dropped.
  useEffect(() => {
    if (!auth.isLoading && (auth.isAuthenticated || auth.error)) {
      navigate('/', { replace: true })
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.error, navigate])

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-500 border-t-transparent mx-auto mb-4" />
        <p className="text-gray-600 dark:text-gray-300">{auth.error ? `Sign-in error: ${auth.error.message}` : 'Signing you in…'}</p>
      </div>
    </div>
  )
}

function App() {
  const [triedSilent, setTriedSilent] = useState(false)
  const auth = useAuth()

  // Keep the axios token store in sync with the current session so API calls
  // carry the bearer token. Runs whenever the auth user changes.
  useEffect(() => {
    setAccessToken(auth.user?.access_token ?? null)
  }, [auth.user])

  // Zero-click SSO: on first load, silently ask the ID server (prompt=none in a hidden
  // iframe) whether there's an existing session. If yes, we sign in with no interaction;
  // if not, the call fails quietly and the user stays logged out (login is optional).
  // Only same-site origins (e.g. *.local.letech.solutions) can pass the cookie here.
  useEffect(() => {
    if (auth.isLoading || auth.isAuthenticated || triedSilent) return
    const path = window.location.pathname
    if (path.startsWith('/callback') || path.startsWith('/silent-renew')) return
    setTriedSilent(true)
    auth.signinSilent().catch(() => { /* no existing session — stay anonymous */ })
  }, [auth, auth.isLoading, auth.isAuthenticated, triedSilent])

  return (
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <Routes>
            {/* OIDC sign-in landing. react-oidc-context processes the code/state on load
                and the AuthCallback component sends the user back to "/", so this is just a spinner. */}
            <Route path="/callback" element={<AuthCallback />} />
            {/* Hidden iframe target for silent token renewal. */}
            <Route path="/silent-renew" element={<div />} />

            <Route
              path="*"
              element={
                <>
                  <AppHeader />
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/courses" element={<CoursesPage />} />
                    <Route path="/courses/new" element={<CourseEditorPage />} />
                    <Route path="/courses/contribute" element={<ContributeCoursePage />} />
                    <Route path="/courses/:id/edit" element={<CourseEditorPage />} />
                    <Route path="/courses/:id" element={<CourseDetailPage />} />
                    <Route path="/courses/:id/edit-gps" element={<EditGPSPage />} />
                    <Route path="/courses/:id/map-editor" element={<CourseMapperPage />} />
                    <Route path="/courses/:id/geometry-import" element={<CourseGeometryImportPage />} />
                    <Route path="/stats" element={<StatsPage />} />
                    <Route path="/rounds" element={<><FourStrokeHoleDemo/><RoundsPage /></>} />
                    {import.meta.env.DEV&&<Route path="/rounds-fixture" element={<RoundReplayFixturePage />} />}
                    {import.meta.env.DEV&&<Route path="/rounds-mapped-fixture" element={<MappedRoundFixturePage />} />}
                    <Route path="/rounds/new" element={<NewRoundPage />} />
                    <Route path="/play" element={<PhoneRoundPage />} />
                    <Route path="/mobile-course-map" element={<MobileCourseMapPage />} />
                    <Route path="/rounds/:id" element={<RoundDetailsPage />} />
                  </Routes>
                </>
              }
            />
          </Routes>
        </div>
      </Router>
  )
}

export default App
